import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const OPTIONS = [
  { key: 'epc', label: 'EPC', sub: 'Energy Performance Certificate' },
  { key: 'gas_safety', label: 'Gas Safety', sub: 'Gas Safety Certificate (CP12)' },
  { key: 'eicr', label: 'EICR', sub: 'Electrical Installation Condition Report' },
  { key: 'not_compliance', label: 'Not a compliance document', sub: 'Keep the file on record, clear this alert' },
];

const btn = (primary, disabled) => ({
  background: disabled ? '#94a3b8' : primary ? '#0073EA' : '#e2e8f0',
  color: primary ? '#fff' : '#0f172a', border: 'none', borderRadius: 8,
  padding: '0.55rem 1.1rem', fontSize: '0.86rem', fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

function OptionCard({ opt, selected, onSelect }) {
  return (
    <button type="button" onClick={() => onSelect(opt.key)}
            data-testid={`classify-option-${opt.key}`}
            aria-pressed={selected}
            style={{ textAlign: 'left', padding: '0.7rem 0.85rem', borderRadius: 10, cursor: 'pointer',
                     border: selected ? '2px solid #0073EA' : '1px solid #cbd5e1',
                     background: selected ? '#eff6ff' : '#fff' }}>
      <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9rem' }}>{opt.label}</div>
      <div style={{ color: '#64748b', fontSize: '0.76rem', marginTop: 2 }}>{opt.sub}</div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', color: '#334155', fontWeight: 600 }}>
      {label}
      {children}
    </label>
  );
}

const inputStyle = { padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.86rem', fontWeight: 400 };

function DocDetails({ doc, docId }) {
  if (!doc) return <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Loading document…</div>;
  return (
    <div data-testid="classify-doc-details"
         style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.7rem 0.85rem', marginBottom: '1rem' }}>
      <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.88rem', wordBreak: 'break-all' }}
           data-testid="classify-doc-filename">{doc.filename || 'Uploaded document'}</div>
      <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: 2 }}
           data-testid="classify-doc-property">{doc.property_address || 'Property unknown'}</div>
      {doc.has_file && (
        <a href={`${API}/api/compliance/doc/${docId}/file`} target="_blank" rel="noopener noreferrer"
           data-testid="classify-doc-view-link"
           style={{ display: 'inline-block', marginTop: 8, color: '#0073EA', fontSize: '0.82rem', fontWeight: 600 }}>
          Open the file in a new tab
        </a>
      )}
    </div>
  );
}

export const ClassifyDocModal = ({ docId, onClose, onResolved }) => {
  const [doc, setDoc] = useState(null);
  const [choice, setChoice] = useState('');
  const [expiry, setExpiry] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/compliance/doc/${docId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('This document could not be loaded.'))))
      .then(setDoc)
      .catch(e => setError(e.message));
  }, [docId]);

  const isCompliance = choice && choice !== 'not_compliance';
  const resolved = doc && doc.doc_type !== 'unclassified';
  const canSubmit = !saving && !resolved && (choice === 'not_compliance' || (isCompliance && expiry));

  const submit = async () => {
    setSaving(true); setError('');
    const body = choice === 'not_compliance'
      ? { not_compliance: true }
      : { doc_type: choice, expiry_date: expiry, issue_date: issueDate || null, issuing_company: company || null };
    try {
      const r = await fetch(`${API}/api/compliance/doc/${docId}/classify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not save. Please try again.');
      onResolved(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="classify-modal-overlay" onClick={() => !saving && onClose()}
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: 12 }}>
      <div onClick={e => e.stopPropagation()} data-testid="classify-modal"
           style={{ background: '#fff', borderRadius: 14, width: 'min(560px, 100%)', maxHeight: '92vh', overflowY: 'auto',
                    padding: '1.25rem 1.4rem', boxShadow: '0 30px 80px rgba(15,23,42,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.8rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Classify this document</h3>
            <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 2 }}>
              We could not tell what this file is. Choose its type to file it, or set it aside.
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} data-testid="classify-modal-close"
                  aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#64748b' }}>
            &times;
          </button>
        </div>

        <DocDetails doc={doc} docId={docId} />

        {resolved ? (
          <div data-testid="classify-already-resolved" style={{ color: '#0f172a', fontSize: '0.86rem', marginBottom: '1rem' }}>
            This document has already been resolved.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: '1rem' }}
               data-testid="classify-options">
            {OPTIONS.map(o => <OptionCard key={o.key} opt={o} selected={choice === o.key} onSelect={setChoice} />)}
          </div>
        )}

        {isCompliance && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: '1rem' }}>
            <Field label="Expiry date (required)">
              <input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} style={inputStyle} data-testid="classify-expiry-date" />
            </Field>
            <Field label="Issue date">
              <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} style={inputStyle} data-testid="classify-issue-date" />
            </Field>
            <Field label="Issuing company">
              <input type="text" value={company} onChange={e => setCompany(e.target.value)} style={inputStyle} data-testid="classify-issuing-company" />
            </Field>
          </div>
        )}

        {error && (
          <div data-testid="classify-error" style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca',
                                                     borderRadius: 8, padding: '0.5rem 0.7rem', fontSize: '0.82rem', marginBottom: '0.8rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={onClose} disabled={saving} style={btn(false, false)} data-testid="classify-cancel-btn">Cancel</button>
          <button type="button" onClick={submit} disabled={!canSubmit} style={btn(true, !canSubmit)} data-testid="classify-submit-btn">
            {saving ? 'Saving…' : choice === 'not_compliance' ? 'Set aside and clear alert' : 'Save classification'}
          </button>
        </div>
      </div>
    </div>
  );
};
